import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, from, Observable, of, throwError } from 'rxjs';
import { first, mergeMap, toArray, map, catchError, tap, shareReplay } from 'rxjs/operators';
import { NotificationService } from '../notifications/notification.service';
import { LgvPricingService } from '../../features/pricing/services/pricing.service';
import { LgvModel } from '../../data-models/longevity.model';
import { LgvPricingModel } from '../../features/pricing/models/pricing.model';
import { environment } from '../../../environments/environment';
import { LocationService } from './location.service';

@Injectable({
  providedIn: 'root',
})
export class LgvTodaysRatesServiceService {
  results: any = {}; // Store the results of best products

  // Default values for the search
  defaultPurchasePrice: number = 280000;
  defaultPurchasePriceJumbo: number = 1667000;
  defaultDownPaymentPercent: number = 25;
  defaultDownPaymentPercentFHA: number = 10;
  defaultDownPaymentPercentJumbo: number = 25;
  defaultLoanType: string = 'Conventional';
  defaultDelta: number = 0.25;
  defaultPrice: number = 100;

  agentId: string;

  isLoadingAgentInfo$ = new BehaviorSubject(false);
  isLoadingResults$ = new BehaviorSubject(false);
  productsReady$ = new BehaviorSubject(false);

  _agentDTO: LgvModel.AgentDTO = null; // Store the agent DTO information
  agentDTO$ = new BehaviorSubject(this._agentDTO);
  companyInfo$ = new BehaviorSubject(null);
  pricingProviderCode$ = new BehaviorSubject('OB');
  agentHasApplyUrl$ = new BehaviorSubject(false);
  loanAppCompany: LgvModel.ApplicationCompany = null;

  _SearchResult: LgvPricingModel.SearchResult = null; // Store the search result
  SearchResult$ = new BehaviorSubject(this._SearchResult);

  // Store the average rates from the API
  averageRates$ = new BehaviorSubject<any[]>([]);

  constructor(
    private http: HttpClient,
    private lgvPricingSvc: LgvPricingService,
    private notificationSvc: NotificationService,
    private locationSvc: LocationService,
  ) { }

  // Simplified init method that just fetches and stores current rates
  init() {
    this.isLoadingResults$.next(true);

    // Fetch average rates and store them
    this.http.get<any[]>(`${environment.LgvApiBaseUrl}/public/pricing/history/current`).pipe(
      first(),
      tap(averageRates => {
        console.log('Fetched average rates:', averageRates);
        this.averageRates$.next(averageRates || []);
      }),
      catchError(err => {
        console.error('Error fetching average rates:', err);
        this.notificationSvc.error('There was an error fetching average rates information.');
        this.isLoadingResults$.next(false);
        return throwError(() => new Error('Error fetching average rates'));
      })
    ).subscribe({
      next: () => {
        this.isLoadingResults$.next(false);
        this.productsReady$.next(true);
      },
      error: () => {
        this.isLoadingResults$.next(false);
      }
    });
  }

  // Gets a live rate and average
  GetProductRateAndAverage(
    agentId: string,
    state: string,
    zipCode: string,
    county: string,
    product: string = 'Conventional',
    term: number = 30,
    purchasePrice?: number,
    downPaymentPercent?: number
  ): Observable<any> {
    this.agentId = agentId;

    const productType = this.getAverageRateNameByProductType(product, term);

    console.log(`Fetching rate for ${productType} with purchasePrice ${purchasePrice} and downPayment ${downPaymentPercent}%`);

    // Wait for averageRates to be available
    return this.averageRates$.pipe(
      first(rates => rates && rates.length > 0),
      mergeMap(averageRates => {
        const matchingAvgRate = averageRates.find(rate => rate.product === productType) || {};
        console.log(`Found matching average rate for ${productType}:`, matchingAvgRate);
        const targetPrice = matchingAvgRate.averagePrice || this.defaultPrice;

        return this.GetProductRate(
          product, term, targetPrice, this.defaultDelta, agentId, purchasePrice, county, state, zipCode, null, downPaymentPercent
        ).pipe(
          map(todaysRate => {
            // Create the rate and average pair
            return {
              product: productType,
              averageRate: {
                averageRateToday: matchingAvgRate.averageRateToday,
                lastCloseDelta: matchingAvgRate.lastCloseDelta,
                timestamp: matchingAvgRate.timestamp,
                averagePrice: matchingAvgRate.averagePrice || this.defaultPrice
              },
              todaysRate: todaysRate || null
            };
          }),
          catchError(error => {
            console.error(`Error fetching rate for ${productType}:`, error);
            return of({
              product: productType,
              averageRate: {
                averageRateToday: matchingAvgRate.averageRateToday || 0,
                lastCloseDelta: matchingAvgRate.lastCloseDelta || 0,
                timestamp: matchingAvgRate.timestamp || new Date(),
                averagePrice: matchingAvgRate.averagePrice || 0
              },
              todaysRate: null
            });
          })
        );
      })
    );
  }

  // Gets a live rate
  GetProductRate(
    loanType: string,
    loanTerm: number,
    targetPrice: number,
    delta: number,
    agentId?: string,
    purchasePrice?: number,
    county?: string,
    state?: string,
    zipCode?: string,
    loanAmount?: number,
    downPaymentPercent?: number,
  ): Observable<any> {
    // Create base model based on loan type
    let model = this.createBaseModel(loanType, loanTerm, purchasePrice, loanAmount, downPaymentPercent);

    // Apply any provided parameters, these will override defaults
    if (agentId) {
      model.agentId = agentId;
    }
    if (zipCode) {
      model.propertyInformation.zipCode = zipCode;
    }
    if (state) {
      model.propertyInformation.state = state;
    }
    if (county) {
      model.propertyInformation.county = county;
    }

    return this.searchProductsWithModel(model, [targetPrice], delta, loanType).pipe(
      map(() => this.results[loanType])
    );
  }

  // Gets the current average rate for a specific loan type and term
  getCurrentAverageRate(loanType: string = 'Conventional', loanTerm: number = 30): Observable<number> {
    const productType = this.getAverageRateNameByProductType(loanType, loanTerm);
    
    return this.averageRates$.pipe(
      first(rates => rates && rates.length > 0),
      map(averageRates => {
        const matchingRate = averageRates.find(rate => rate.product === productType);
        if (matchingRate && matchingRate.averageRateToday) {
          console.log(`Found average rate for ${productType}:`, matchingRate.averageRateToday);
          return matchingRate.averageRateToday;
        } else {
          console.warn(`No average rate found for ${productType}, using default 6.5%`);
          return 6.5; // Default fallback rate
        }
      }),
      catchError(error => {
        console.error('Error getting current average rate:', error);
        return of(6.5); // Default fallback rate
      })
    );
  }

  private getAverageRateNameByProductType(productType: string, term: number): string {
    const termString = term === 15 ? '15 YEAR' : '30 YEAR';
    switch (productType.toLowerCase()) {
      case 'conventional':
        return `${termString} CONFORMING`;
      case 'jumbo':
      case 'nonconforming':
        return `${termString} JUMBO`;
      default:
        return `${termString} ${productType.toUpperCase()}`;
    }
  }
  
  private createBaseModel(loanType: string, loanTerm: number, purchasePrice?: number, loanAmount?: number, downPaymentPercent?: number): LgvPricingModel.ProductSearch {
    const model = this.lgvPricingSvc.defaultOBProductSearch();

    // Set loan term
    model.loanInformation.loanTerms = [loanTerm === 15 ? 'FifteenYear' : 'ThirtyYear'];

    // Set loan type specific defaults
    switch (loanType.toLowerCase()) {
      case 'fha':
        model.loanInformation.loanType = 'FHA';
        model.loanInformation.representativeFICO = model.borrowerInformation.fico = 680;
        break;
      case 'va':
        model.loanInformation.loanType = 'VA';
        model.loanInformation.representativeFICO = model.borrowerInformation.fico = 680;
        break;
      case 'jumbo':
      case 'nonconforming':
        model.loanInformation.loanType = 'NonConforming';
        model.borrowerInformation.monthsReserves = 12;
        break;
      case 'conventional':
      default:
        model.loanInformation.loanType = 'Conventional';
        break;
    }

    // Set the purchase price
    if (!purchasePrice) {
      if (model.loanInformation.loanType == 'NonConforming') {
        model.propertyInformation.salesPrice = model.propertyInformation.appraisedValue = this.defaultPurchasePriceJumbo;
      } else {
        model.propertyInformation.salesPrice = model.propertyInformation.appraisedValue = this.defaultPurchasePrice;
      }
    }
    else {
      model.propertyInformation.salesPrice = model.propertyInformation.appraisedValue = purchasePrice;
    }

    // Set the loan amount
    if (!loanAmount) {
      if (!model?.loanInformation?.downPaymentPercent || !downPaymentPercent) {
        if (loanType.toLowerCase() === 'jumbo') {
          model.loanInformation.downPaymentPercent = this.defaultDownPaymentPercentJumbo;
        } else if (loanType.toLowerCase() === 'fha') {
          model.loanInformation.downPaymentPercent = this.defaultDownPaymentPercentFHA;
        } else {
          model.loanInformation.downPaymentPercent = this.defaultDownPaymentPercent;
        }
      }
      else {
        model.loanInformation.downPaymentPercent = downPaymentPercent;
      }

      const downPaymentAmount = model.propertyInformation.salesPrice * ((model?.loanInformation?.downPaymentPercent ?? 0) / 100);
      const loanAmount = model.propertyInformation.salesPrice - downPaymentAmount;

      model.loanInformation.baseLoanAmount = model.loanInformation.totalLoanAmountDetails.totalLoanAmount = loanAmount;
    }
    else {
      model.loanInformation.baseLoanAmount = model.loanInformation.totalLoanAmountDetails.totalLoanAmount = loanAmount;
    }

    return model;
  }

  // Search products for a specific model (loan type)
  private searchProductsWithModel(
    model: LgvPricingModel.ProductSearch,
    targetPrices: number[],
    delta: number,
    loanTypeKey: string
  ): Observable<any> {

    if (!model.loanInformation.loanPurpose) {
      model.loanInformation.loanPurpose = 'Purchase';
    }
    if (!model.loanInformation.desiredPrice) {
      model.loanInformation.desiredPrice = targetPrices[0] ?? 100;
    }

    // Create an observable for location lookup
    const locationLookup$ = !model.propertyInformation.zipCode
      ? this.locationSvc.geoLocationLookup().pipe(
        tap((location: LgvModel.locationInfo) => {
          if (location) {
            model.propertyInformation.zipCode = location.postalCode;
            model.propertyInformation.state = location.stateCode;
            model.propertyInformation.county = location.county;
          }
        })
      )
      : !model.propertyInformation.state || !model.propertyInformation.county
        ? this.locationSvc.lookupLocationByZip(model.propertyInformation.zipCode).pipe(
          tap((location: LgvModel.locationInfo) => {
            if (location) {
              model.propertyInformation.state = location.stateCode;
              model.propertyInformation.county = location.county;
            }
          })
        )
        : of(null);

    // Chain the location lookup with the product search
    return locationLookup$.pipe(
      mergeMap(() => from(targetPrices)),
      mergeMap((target) => {
        model.version = `${loanTypeKey} | ${target} | ${delta} | v1.76 | ${model.propertyInformation.state}`;
        return this.lgvPricingSvc
          .todaysRatesProductSearchPublic(this?.agentDTO$?.value?.agentInfo?.id || model?.agentId, model)
          .pipe(
            first(),
            mergeMap((result) => {
              this.findBestQuotesFromResults(target, result, loanTypeKey, model);
              return [result];
            })
          );
      }),
      toArray()
    );
  }

  // Find the best quotes from the results and store them by loan type key
  private findBestQuotesFromResults(targetPrice: number, results: LgvPricingModel.SearchResult, loanTypeKey: string, model: any) {
    if (!results || !results.products || results.products.length === 0) {
      console.warn(`No products found for ${loanTypeKey}`);
      return;
    }

    // Find best result for the loan type
    const bestResult = this.findBestRate(results.products, targetPrice);
    if (!bestResult) {
      console.warn(`No best rate found for ${loanTypeKey} at target price ${targetPrice}`);
      return;
    }

    // Store the best result under the corresponding loan type key
    if (!this.results[loanTypeKey]) {
      this.results[loanTypeKey] = {};
    }
    this.results[loanTypeKey] = bestResult;
    this.results[loanTypeKey]['model'] = model;
    this.results[loanTypeKey]['targetPrice'] = targetPrice;
  }

  // Find the best rate equal to or above target price
  private findBestRate(products: LgvPricingModel.Product[], targetPrice: number): LgvPricingModel.Product {
    if (!products || products.length === 0) {
      console.warn('No products provided to findBestRate');
      return null;
    }

    try {
      let sortedProducts = products.sort((a, b) => this.productCompareOrder(a, b));
      let resultsInTargetRange = sortedProducts.filter(x => x && x.price >= targetPrice);

      // if results in target range, return the best rate (1st item)
      if (resultsInTargetRange.length > 0) {
        return resultsInTargetRange[0];
      }

      // all products are below target price, so find the highest price (new target price), and find the best rate at that price
      let sortedByPrice = products.sort((a, b) => {
        if (!a || !b) return 0;
        return a.price > b.price ? -1 : 1;
      });

      if (sortedByPrice.length === 0) {
        console.warn('No products after sorting by price');
        return null;
      }

      let adjTargetPrice = sortedByPrice[0].price;
      let resultsInAdjustedTargetRange = sortedProducts.filter(x => x && x.price >= adjTargetPrice);

      // should be at least one...
      return resultsInAdjustedTargetRange[0] || null;
    } catch (error) {
      console.error('Error in findBestRate:', error);
      return null;
    }
  }

  // Compare products to find the best match based on rate, APR, and price
  private productCompareOrder(a: LgvPricingModel.Product, b: LgvPricingModel.Product): number {
    if (!a || !b) return 0;
    return (
      (a.rate < b.rate ? -1 : 1) ||
      (a.apr < b.apr ? -1 : 1) ||
      (a.price >= b.price ? -1 : 1)
    );
  }
}
